import { useState } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { AddressList } from "@/components/settings/profile/delivery/AddressList";
import { AddressForm } from "@/components/settings/profile/delivery/AddressForm";
import { UserManagementService } from "@/services/user/userManagementService";
import { settingsLabels } from "@/i18n";
import { getLanguage } from "@/utils/languageUtils";
import { userLabels } from "@/i18n";

interface AdminUserDeliverySectionProps {
  userId: string;
  deliveryAddresses: any[];
  isLoading: boolean;
}

export const AdminUserDeliverySection = ({
  userId,
  deliveryAddresses,
  isLoading,
}: AdminUserDeliverySectionProps) => {
  const queryClient = useQueryClient();
  const labels = settingsLabels(getLanguage());
  const userLabels_i18n = userLabels();
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [addressToDelete, setAddressToDelete] = useState<string | null>(null);
  const [editAddressId, setEditAddressId] = useState<string | null>(null);

  const addresses = deliveryAddresses || [];
  const refetch = () => queryClient.invalidateQueries({ queryKey: ["userDeliveryAddresses", userId] });

  const handleSetDefault = async (addressId: string) => {
    try {
      await UserManagementService.adminSetDefaultDeliveryAddress(userId, addressId);
      toast.success(labels.delivery.defaultUpdated || "Standardadress uppdaterad");
      refetch();
    } catch (error) {
      toast.error(labels.delivery.errorUpdating || "Kunde inte uppdatera");
    }
  };

  const handleDeleteAddress = (addressId: string) => {
    if (addresses.length <= 1) {
      toast.error(labels.delivery.cannotDeleteLast || "Användaren måste ha minst en leveransadress");
      return;
    }
    setAddressToDelete(addressId);
    setShowDeleteDialog(true);
  };

  const confirmDeleteAddress = async () => {
    if (addressToDelete) {
      try {
        await UserManagementService.adminDeleteDeliveryAddress(userId, addressToDelete);
        toast.success(labels.delivery.addressDeleted || "Adress borttagen");
        setShowDeleteDialog(false);
        setAddressToDelete(null);
        refetch();
      } catch (error) {
        toast.error(labels.delivery.errorDeleting || "Kunde inte ta bort");
      }
    }
  };

  const handleAddAddress = async (address: {
    street: string;
    postalCode: string;
    city: string;
    country?: string;
    isDefault?: boolean;
  }) => {
    try {
      await UserManagementService.adminCreateDeliveryAddress(userId, address);
      toast.success(labels.delivery.addressAdded || "Adress tillagd");
      refetch();
    } catch (error) {
      toast.error(labels.delivery.errorAdding || "Kunde inte lägga till");
    }
  };

  const handleUpdateAddress = async (address: {
    street: string;
    postalCode: string;
    city: string;
    country?: string;
    isDefault?: boolean;
  }) => {
    if (editAddressId) {
      try {
        await UserManagementService.adminUpdateDeliveryAddress(userId, editAddressId, address);
        toast.success(labels.delivery.addressUpdated || "Adress uppdaterad");
        setEditAddressId(null);
        refetch();
      } catch (error) {
        toast.error(labels.delivery.errorUpdating || "Kunde inte uppdatera");
      }
    }
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-base">{userLabels_i18n.deliveryAddresses}</CardTitle>
      </CardHeader>
      <CardContent>
        <div className="space-y-4">
          {isLoading ? (
            <p className="text-sm text-muted-foreground">{labels.delivery.loading || "Laddar..."}</p>
          ) : (
            <>
              <AddressList
                addresses={addresses}
                onSetDefault={handleSetDefault}
                onDelete={handleDeleteAddress}
                onEdit={setEditAddressId}
              />
              <AddressForm
                onAddressAdded={handleAddAddress}
                onAddressUpdated={handleUpdateAddress}
                editAddressId={editAddressId}
                addresses={addresses}
                onCancelEdit={() => setEditAddressId(null)}
              />
            </>
          )}
        </div>
      </CardContent>

      <Dialog open={showDeleteDialog} onOpenChange={setShowDeleteDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{labels.delivery.deleteAddress || "Radera adress"}</DialogTitle>
          </DialogHeader>
          <p>{labels.delivery.deleteAddressConfirm || "Är du säker på att du vill radera denna adress?"}</p>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowDeleteDialog(false)}>
              {labels.delivery.cancel || "Avbryt"}
            </Button>
            <Button variant="destructive" onClick={confirmDeleteAddress}>
              {labels.delivery.delete || "Radera"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </Card>
  );
};
